import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, CalendarCheck } from "lucide-react";
import { SERVICES, MAIN_SERVICES } from "@/lib/content";
import { ServiceCard } from "@/components/site/Services";
import { ServiceGallery } from "@/components/site/ServiceGallery";

const ServiceHero = ({ svc }) => (
  <section className="relative pt-[100px] min-h-[80svh] flex items-end overflow-hidden bg-noir" data-testid="service-hero">
    <img src={svc.image} alt={svc.title} className="absolute inset-0 w-full h-full object-cover opacity-70" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10" />
    <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="relative max-w-[1440px] w-full mx-auto px-5 sm:px-8 pb-20">
      <p className="eyebrow text-rose">{svc.kicker}</p>
      <h1 className="mt-5 font-display text-white text-4xl sm:text-5xl lg:text-7xl leading-[1] max-w-4xl" data-testid="service-title">{svc.title}</h1>
      <p className="mt-6 max-w-xl text-white/80 text-base sm:text-lg leading-relaxed">{svc.intro}</p>
      <Link to="#cita" className="btn-shine mt-10 inline-flex items-center gap-2 rounded-full bg-granate text-white px-7 py-4 font-brand text-xs font-semibold tracking-[0.18em] uppercase" data-testid="service-agenda-btn">
        <CalendarCheck className="w-4 h-4" /> Agenda una cita
      </Link>
    </motion.div>
  </section>
);

const Includes = ({ svc }) => (
  <section className="py-24 max-w-[1440px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
    <div className="lg:col-span-4">
      <p className="eyebrow text-granate flex items-center gap-3"><span className="w-8 h-px bg-granate" />Qué incluye</p>
      <h2 className="mt-6 font-display text-4xl sm:text-5xl">{svc.name}</h2>
    </div>
    <ul className="lg:col-span-8 grid sm:grid-cols-2 gap-4" data-testid="service-includes">
      {svc.points.map((p) => (
        <li key={p} className="flex items-start gap-4 rounded-2xl bg-white p-6 border border-ink/5">
          <span className="w-8 h-8 shrink-0 rounded-full bg-blush grid place-items-center text-granate"><Check className="w-4 h-4" /></span>
          <span className="font-display text-xl">{p}</span>
        </li>
      ))}
    </ul>
  </section>
);

export default function ServicePage({ serviceKey }) {
  const svc = SERVICES[serviceKey];
  const subs = svc.children || (svc.parent ? SERVICES[svc.parent].children.filter((c) => c !== serviceKey) : null);
  const others = MAIN_SERVICES.filter((k) => k !== serviceKey && k !== svc.parent).slice(0, 3);
  return (
    <div data-testid={`service-page-${serviceKey}`}>
      <ServiceHero svc={svc} />
      <Includes svc={svc} />
      <ServiceGallery svc={svc} />
      <section className="bg-sand py-24">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <h2 className="font-display text-4xl sm:text-5xl mb-10">{subs && svc.children ? "Elige tu celebración" : "También te puede interesar"}</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {(subs || others).map((k, i) => <ServiceCard key={k} k={k} i={i} className="min-h-[320px]" />)}
          </div>
        </div>
      </section>
      <section className="py-20 text-center" data-testid="service-closing">
        <h2 className="font-display text-4xl sm:text-5xl">¿Empezamos por la fecha?</h2>
        <Link to="#cita" className="btn-shine mt-8 inline-flex items-center gap-2 rounded-full bg-granate text-white px-7 py-4 font-brand text-xs font-semibold tracking-[0.18em] uppercase" data-testid="service-closing-btn">
          <CalendarCheck className="w-4 h-4" /> Agenda una cita
        </Link>
      </section>
    </div>
  );
}
